// Pure helpers for the supervised member's side on the web: which of their
// own actions a supervision gates (ask → an "Ask" button, notify → a heads-up),
// their requests' targets and state, the consent screens' wording and the
// errors accepting can meet.
import type { Schemas } from "@10twenty/api";
import { CONTROLS, modeOf } from "./supervision";

type Control = Schemas["SupervisionControl"];
type Mode = Schemas["SupervisionMode"];
type Controls = Schemas["SupervisionControls"];
type Action = Schemas["SupervisionAction"];
type Params = Schemas["SupervisionRequestParams"];
type Request = Schemas["SupervisionRequest"];
type Circle = Pick<Schemas["Circle"], "id" | "supervision"> & Partial<Pick<Schemas["Circle"], "supervision_offer">>;

/** The control that gates each action, and whether it is decided per circle or across the account. */
export const ACTION_CONTROLS: Record<Action, { control: Control; scope: "circle" | "account" }> = {
  pause_circle: { control: "pause", scope: "circle" },
  pause_global: { control: "pause", scope: "account" },
  bubble_circle: { control: "approximate", scope: "circle" },
  bubble_everywhere: { control: "approximate", scope: "account" },
  leave: { control: "leave", scope: "circle" },
  delete_account: { control: "delete_account", scope: "account" },
  join_circle: { control: "join_circles", scope: "account" },
  create_circle: { control: "join_circles", scope: "account" },
};

const RANK: Record<Mode, number> = { allow: 0, notify: 1, ask: 2 };

/** The strictest of `modes` (none: allow). */
export function strictest(modes: Iterable<Mode>): Mode {
  let result: Mode = "allow";
  for (const mode of modes) if (RANK[mode] > RANK[result]) result = mode;
  return result;
}

/**
 * The mode the caller's own supervisions set for `action`, as the server's
 * gate reads it: the circle's own supervision for circle actions, the
 * strictest over every circle for account-wide ones. `circleId` names the
 * circle of a circle action; a circle missing from `circles` reads allow.
 */
export function actionMode(action: Action, circles: readonly Circle[], circleId?: string | null): Mode {
  const { control, scope } = ACTION_CONTROLS[action];
  const relevant = scope === "circle" ? circles.filter((c) => c.id === circleId) : circles;
  return strictest(relevant.flatMap((c) => (c.supervision ? [modeOf(c.supervision.controls, control)] : [])));
}

/** How often circle data is refetched while it holds the caller's supervision or an offer (guardians may change it). */
export const SUPERVISION_POLL_MS = 30_000;

/** `refetchInterval` for a circle (or the circle list): poll while the caller is supervised or offered supervision. */
export function supervisionPoll(data: Circle | readonly Circle[] | undefined): number | false {
  const list: readonly Circle[] = data === undefined ? [] : Array.isArray(data) ? data : [data as Circle];
  return list.some((c) => c.supervision || c.supervision_offer) ? SUPERVISION_POLL_MS : false;
}

/** Whether any of `circles` supervises the caller. */
export const isSupervised = (circles: readonly Circle[]): boolean => circles.some((c) => c.supervision);

/** A bubble's action: for one circle, or for every circle. */
export const bubbleAction = (circleId: string | null): Action => (circleId ? "bubble_circle" : "bubble_everywhere");

/** A bubble request's params, as the bubble form chose them (radii and durations are the form's own choices). */
export function bubbleParams(circleId: string | null, radiusM: number, hours: number | null): Params {
  return {
    ...(circleId && { circle_id: circleId }),
    radius_m: radiusM as NonNullable<Params["radius_m"]>,
    duration_hours: hours as Params["duration_hours"] & (number | null),
  };
}

/**
 * The key the server keeps one pending request per (DESIGN.md "Requests"):
 * the circle, the account, or the new circle's kind and name. A join is keyed
 * by the circle joined, which the member's view doesn't name, so the invite
 * code stands in (Ask controls also show the request the server returned).
 */
export function requestTarget(action: Action, params: Params | undefined): string {
  switch (action) {
    case "pause_circle":
    case "bubble_circle":
    case "leave":
      return `${action}:${params?.circle_id ?? ""}`;
    case "join_circle":
      return `${action}:${(params?.invite_code ?? "").toUpperCase()}`;
    case "create_circle":
      return `${action}:${params?.circle?.kind ?? "family"}:${(params?.circle?.name ?? "").trim().toLowerCase()}`;
    default:
      return action;
  }
}

/** How long a decided request stays shown beside its button. */
export const RECENT_REQUEST_MS = 60 * 60_000;

/**
 * The request to show beside the button for `action` on this target: the
 * pending one, else the newest decided within `RECENT_REQUEST_MS` (never one
 * the member withdrew), else null. `requests` are newest first, as listed.
 */
export function requestFor(
  requests: readonly Request[],
  action: Action,
  params: Params | undefined,
  nowMs: number,
): Request | null {
  const target = requestTarget(action, params);
  const matching = requests.filter((r) => r.action === action && requestTarget(r.action, r.params) === target);
  const pending = matching.find((r) => isPending(r, nowMs));
  if (pending) return pending;
  const recent = matching.find(
    (r) =>
      r.status !== "pending" &&
      r.status !== "cancelled" &&
      r.decided_at !== null &&
      nowMs - Date.parse(r.decided_at) < RECENT_REQUEST_MS,
  );
  return recent ?? null;
}

/** Requests for any of `actions` worth showing: pending, or decided in the last hour (not withdrawn); newest first. */
export function recentRequests(requests: readonly Request[], actions: readonly Action[], nowMs: number): Request[] {
  return requests.filter(
    (r) =>
      actions.includes(r.action) &&
      (isPending(r, nowMs) ||
        (r.status !== "pending" &&
          r.status !== "cancelled" &&
          r.decided_at !== null &&
          nowMs - Date.parse(r.decided_at) < RECENT_REQUEST_MS)),
  );
}

/** Pending and not yet past its expiry (the sweep may lag). */
export const isPending = (request: Pick<Request, "status" | "expires_at">, nowMs: number): boolean =>
  request.status === "pending" && Date.parse(request.expires_at) > nowMs;

/** The caller's pending requests, oldest first; `circleId` keeps those this circle has a say in. */
export function pendingRequests(requests: readonly Request[], nowMs: number, circleId?: string): Request[] {
  return requests
    .filter((r) => isPending(r, nowMs))
    .filter((r) => circleId === undefined || r.approvals.some((a) => a.circle_id === circleId))
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
}

/** What the member asks for, in their own words. */
export function myRequestText(
  request: Pick<Request, "action" | "params">,
  circleName?: (id: string) => string | undefined,
): string {
  const { params } = request;
  const named = params.circle_id ? circleName?.(params.circle_id) : undefined;
  const here = named ? `“${named}”` : "this circle";
  switch (request.action) {
    case "pause_circle":
      return `Pause sharing with ${here}${params.pause_hours ? ` for ${params.pause_hours === 1 ? "1 hour" : `${params.pause_hours} hours`}` : ""}`;
    case "pause_global":
      return "Pause sharing everywhere";
    case "bubble_circle":
      return `Share an approximate area with ${here}`;
    case "bubble_everywhere":
      return "Share an approximate area with every circle";
    case "leave":
      return `Leave ${here}`;
    case "delete_account":
      return "Delete your account";
    case "join_circle":
      return "Join another circle";
    case "create_circle":
      return params.circle?.name ? `Create “${params.circle.name}”` : "Create a circle";
    default:
      return "A request";
  }
}

const MY_FAILURES: Record<string, string> = {
  circle_full: "the circle is full",
  not_found: "the invite no longer works",
  invite_expired: "the invite no longer works",
  already_member: "you're already in that circle",
  sos_open: "you have an SOS open",
  event_limit: "you already organise as many events as allowed",
  last_admin: "you're the circle's only admin",
  event_ended: "the event has ended",
  sharing_paused: "your sharing is paused",
  approximate_sharing: "you share only an approximate area",
};

const timeText = (iso: string, locale?: string) =>
  new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });

/** Where the member's request stands; `pending` keeps its Withdraw button. */
export function myRequestState(
  request: Pick<Request, "status" | "error" | "expires_at" | "decided_by" | "decision_note" | "approvals">,
  nowMs: number,
  locale?: string,
): { text: string; pending: boolean; tone: "muted" | "good" | "bad" } {
  const by = request.decided_by ? ` by ${request.decided_by.name}` : "";
  const note = request.decision_note ? `: “${request.decision_note}”` : "";
  if (isPending(request, nowMs)) {
    const total = request.approvals.length;
    const done = request.approvals.filter((a) => a.approved_at).length;
    const progress = total > 1 && done > 0 ? ` (approved in ${done} of ${total} circles)` : "";
    return {
      text: `Asked · waiting for a guardian${progress}. The request lapses at ${timeText(request.expires_at, locale)} if nobody answers.`,
      pending: true,
      tone: "muted",
    };
  }
  switch (request.status) {
    case "approved":
      return { text: `Approved${by}${note}. Done.`, pending: false, tone: "good" };
    case "declined":
      return { text: `Declined${by}${note}.`, pending: false, tone: "bad" };
    case "failed":
      return {
        text: `Approved${by}, but it couldn't be done: ${(request.error && MY_FAILURES[request.error]) ?? "the server refused it"}. You can ask again.`,
        pending: false,
        tone: "bad",
      };
    case "stale":
      return {
        text: "Your supervision changed since you asked, so the request no longer applies. Ask again.",
        pending: false,
        tone: "bad",
      };
    case "pending":
    case "expired":
      return { text: "Nobody answered within 24 hours. You can ask again.", pending: false, tone: "bad" };
    case "cancelled":
      return { text: "You withdrew this request.", pending: false, tone: "muted" };
    default:
      return { text: "This request is closed.", pending: false, tone: "muted" };
  }
}

/** Errors sending a request, worth explaining; null for the generic message. */
export function askErrorText(code: string | undefined, status?: number): string | null {
  if (code === "request_not_needed") return "Your guardians don't need to approve this any more. Try it again.";
  if (status === 429) return "You've sent as many requests as you can today. Try again tomorrow.";
  if (code === "already_member") return "You're already in that circle.";
  return null;
}

export const MY_CONTROL_LABELS: Record<Control, string> = {
  pause: "Pausing sharing",
  approximate: "Sharing an approximate area",
  leave: "Leaving the circle",
  delete_account: "Deleting your account",
  join_circles: "Joining or creating other circles",
};

export const MY_MODE_LABELS: Record<Mode, string> = {
  allow: "You decide",
  notify: "You decide; your guardians are told",
  ask: "You ask a guardian first",
};

/** The controls a supervision sets, each with the member's wording; allow-only controls are left out unless `all`. */
export function myControlRows(
  controls: Controls,
  all = false,
): { control: Control; label: string; mode: Mode; text: string }[] {
  return CONTROLS.map((control) => ({ control, mode: modeOf(controls, control) }))
    .filter((row) => all || row.mode !== "allow" || controls[row.control] !== undefined)
    .map((row) => ({ ...row, label: MY_CONTROL_LABELS[row.control], text: MY_MODE_LABELS[row.mode] }));
}

/** The consent screen's promises: what guardians can and can't do, and the way out. */
export function consentLines(guardianNames: string, circleName: string, selfReleaseDays: number): string[] {
  const days = selfReleaseDays === 1 ? "1 day" : `${selfReleaseDays} days`;
  return [
    `${guardianNames} can pause your sharing with ${circleName}, resume it, or share an approximate area for you there, and decide your requests. You're told every time.`,
    `They can't read your chats, see other circles or change your sharing anywhere else, and they never see which other circles you're in.`,
    `You can stop supervision on your own at any time: it ends ${days} after you ask, and nobody can change that. Your guardians are told when you ask.`,
    "SOS alerts and emergency contacts work exactly as before.",
  ];
}

/** Why accepting supervision (an offer or a supervised invite) was refused; null for the generic message. */
export function acceptErrorText(code: string | undefined): string | null {
  switch (code) {
    case "sharing_paused":
      return "You've paused sharing everywhere, and with this supervision that pause would need a guardian's approval. Resume sharing in account settings first, then accept.";
    case "approximate_sharing":
      return "You share an approximate area with every circle, and with this supervision that would need a guardian's approval. Turn it off in account settings first (an area for one circle is fine), then accept.";
    case "supervision_consent_required":
      return "The supervision changed since this page loaded. Check what it says now and accept again.";
    case "supervision_unavailable":
      return "Supervision isn't available here right now. Ask whoever invited you for an ordinary invite.";
    case "already_member":
      return "You're already in this circle. A supervised invite is for new members; an admin can offer you supervision instead.";
    case "supervised_member":
      return "You're an admin here, and admins can't be supervised.";
    default:
      return null;
  }
}

/** "Your guardians are told." for a notify-mode action; null otherwise. */
export const noticeText = (mode: Mode): string | null => (mode === "notify" ? "Your guardians are told." : null);

/** When the member's self-release ends supervision. */
export function releaseText(releaseAt: string, locale?: string): string {
  const at = new Date(releaseAt).toLocaleString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `You asked to stop supervision. It ends on ${at}; your guardians can't change that.`;
}
